import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { FinancialSettlementAdvancePayment } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { Money } from '@dreryk/money-common-api';

import { FormContextValues, Translate, useFormController } from '@dreryk/react-common';
import { FormController, Heading, StyledFlex, StyledMargin } from '@dreryk/react-components';

import { visitSettlementTranslationKeys } from '../../translations';
import { VisitMedicalServiceInteractiveTable } from '../medicalServices/VisitMedicalServiceInteractiveTable';
import { VisitSettlementServiceList } from '../medicalServices/VisitSettlementServiceList';
import { getMedicalServicePrices } from '../medicalServices/medicalServices.utils';
import { PriceChangeReasonField } from '../priceChangeReason/PriceChangeReasonField';

import { StyledForm, StyledHeading } from './VisitSettlementForm.styled';
import {
   areAdvancePaymentsAvailable,
   medicalServiceSectionVisible,
   showAdjustmentNote,
   showAdvancePaymentSection,
   showMedicalServicesList,
   showMedicalServicesTable,
   showPaymentCorrectlyCheckbox,
   showPaymentEnabledToggle,
   showPaymentFormPart,
   showPaymentMethodDescriptionInput,
   showPractitionerTextView,
   showPreviousAdjustmentNote,
   showPriceChangeReasonField,
   showPriceChangeReasonText,
   showVisitDetailsFormPart,
} from './VisitSettlementForm.utils';
import { VisitSettlementFormMedicalServicesPrices } from './VisitSettlementFormMedicalServicesPrices';
import { AdjustmentNoteField } from './adjustment/AdjustmentNoteField';
import { PreviousAdjustmentNote } from './adjustment/PreviousAdjustmentNoteTextView';
import { AdvancePaymentTextView } from './advancePayment/AdvancePaymentTextView';
import { PaymentAcceptedCorrectlyCheckbox } from './payment/PaymentAcceptedCorrectlyCheckbox';
import { PaymentAmountInput } from './payment/PaymentAmountInput';
import { PaymentEnabledToggle } from './payment/PaymentEnabledToggle';
import { PaymentMethodDescriptionInput } from './payment/PaymentMethodDescriptionInput';
import { PaymentMethodRadioGroup } from './payment/PaymentMethodRadioGroup';
import { PaymentController } from './payment/PaymentRequiredController';
import { visitSettlementPaymentStorage } from './payment/payment.storage';
import { PriceChangeReasonTextView } from './priceChangeReason/PriceChangeReasonTextView';
import { TaxIdentifierForReceiptField } from './taxtIdentifierForReceipt/TaxIdentifierForReceiptField';
import { PatientHeading } from './visitDetails/PatientHeading';
import { PractitionerTextView } from './visitDetails/PractitionerTextView';
import { VisitDate } from './visitDetails/VisitDate';
import { VisitSettlementFormBean } from './visitSettlementForm.types';

export type VisitSettlementFormController = FormContextValues<VisitSettlementFormBean>;

export type VisitSettlementFormInitializer = (appointmentId: number) => VisitSettlementFormBean;

type VisitSettlementFormRendererProps = Pick<VisitSettlementFormController, 'handleSubmit' | 'formState'> &
   Pick<VisitSettlementFormBean, 'payment'>;

type VisitSettlementFormRenderer = (renderProps: VisitSettlementFormRendererProps) => JSX.Element;

type VisitSettlementFormProps = {
   readonly initializer: VisitSettlementFormInitializer;
   readonly appointmentId: number;
   readonly practitionerId: number;
   readonly children: VisitSettlementFormRenderer;
   readonly fiscalPrintEnabled?: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const VisitSettlementForm = (props: VisitSettlementFormProps): JSX.Element => {
   const {
      register,
      handleSubmit,
      formState,
      watch,
      control,
      trigger,
      setValue,
   } = useFormController<VisitSettlementFormBean>(
      // eslint-disable-next-line react-hooks/exhaustive-deps
      useMemo(() => props.initializer(props.appointmentId), [props.initializer, props.appointmentId])
   );

   const { visitDetails, visitSettlement, payment, adjustment } = watch([
      'visitDetails',
      'visitSettlement',
      'payment',
      'adjustment',
   ]);

   useUpdateEffect(() => {
      visitSettlementPaymentStorage.setEnabledPreference(payment.enabled);
   }, [payment.enabled]);

   const handleRefundSuccess = (updatedRemainingAmount: number, advancePaymentId: number): void => {
      const { advancePayments = [] } = visitSettlement;

      const updatedAdvancePayments = advancePayments.map(
         (advancePayment): FinancialSettlementAdvancePayment =>
            advancePayment.initialTransactionId === advancePaymentId
               ? { ...advancePayment, remainingAmount: Money.of(updatedRemainingAmount) }
               : advancePayment
      );

      const updatedVisitSettlement = { ...visitSettlement, advancePayments: updatedAdvancePayments };

      return setValue('visitSettlement', updatedVisitSettlement);
   };

   return (
      <>
         <StyledForm noValidate autoComplete={'off'}>
            {showVisitDetailsFormPart(visitDetails) && (
               <>
                  <FormController controller={register('visitDetails')} />
                  <div>
                     <PatientHeading
                        firstName={visitDetails.patient.firstName}
                        lastName={visitDetails.patient.lastName}
                     />
                     <VisitDate beginDate={visitDetails.beginDate} endDate={visitDetails.endDate} />
                  </div>

                  {showPractitionerTextView(visitDetails, adjustment) && (
                     <PractitionerTextView practitioner={visitDetails.practitioner} />
                  )}
               </>
            )}
            <FormController controller={register('visitSettlement')} />
            {medicalServiceSectionVisible(visitSettlement) && (
               <>
                  {showMedicalServicesList(visitSettlement) && (
                     <div>
                        <StyledHeading
                           testId={'medicalServices'}
                           type={'h3'}
                           margin={'8px'}
                           content={
                              visitSettlementTranslationKeys.shared.form.sections.visitSettlement.medicalServices.header
                           }
                           bold
                        />
                        <VisitSettlementServiceList
                           adjustment={adjustment}
                           medicalServices={visitSettlement.medicalServices}
                           advancePayments={visitSettlement.advancePayments}
                           visitDetails={visitDetails}
                           onRefundSuccess={handleRefundSuccess}
                        />
                     </div>
                  )}
                  {showMedicalServicesTable(visitSettlement) && (
                     <div>
                        <Heading
                           testId={'medicalServices'}
                           type={'h2'}
                           content={
                              visitSettlementTranslationKeys.shared.form.sections.visitSettlement.medicalServices.header
                           }
                        />
                        {areAdvancePaymentsAvailable(visitSettlement.advancePayments) ? (
                           <StyledFlex gap={12} direction={'column'}>
                              <VisitMedicalServiceInteractiveTable
                                 control={control}
                                 practitionerId={props.practitionerId}
                                 register={register}
                              />
                              <VisitSettlementFormMedicalServicesPrices
                                 adjustment={adjustment}
                                 editable={visitSettlement.editable}
                                 advancePayments={visitSettlement.advancePayments}
                                 medicalServices={visitSettlement.medicalServices}
                                 visitDetails={visitDetails}
                                 onRefundSuccess={handleRefundSuccess}
                              />
                           </StyledFlex>
                        ) : (
                           <VisitMedicalServiceInteractiveTable
                              control={control}
                              practitionerId={props.practitionerId}
                              register={register}
                              bottomElement={
                                 <VisitSettlementFormMedicalServicesPrices
                                    editable={visitSettlement.editable}
                                    medicalServices={visitSettlement.medicalServices}
                                 />
                              }
                           />
                        )}
                     </div>
                  )}
               </>
            )}
            {showPriceChangeReasonText(visitSettlement, adjustment) && (
               <PriceChangeReasonTextView value={visitSettlement.priceChangeReason} />
            )}
            {showPriceChangeReasonField(visitSettlement, adjustment) && (
               <div>
                  <Heading
                     testId={'priceChangeReason'}
                     type={'h2'}
                     content={
                        visitSettlementTranslationKeys.shared.form.sections.visitSettlement.priceChangeReason.header
                     }
                  />
                  <PriceChangeReasonField name={'visitSettlement.priceChangeReason'} register={register} />
               </div>
            )}
            <FormController controller={register('adjustment')} defaultValue={null} />
            {showPreviousAdjustmentNote(adjustment) && (
               <PreviousAdjustmentNote previousAdjustment={adjustment.previousAdjustment} />
            )}
            {showAdjustmentNote(adjustment) && <AdjustmentNoteField register={register} />}
            <div>
               <PaymentController register={register} name={'required'} />
               <Heading
                  testId={'payment'}
                  type={'h2'}
                  content={
                     <StyledFlex gap={'8px'} alignItems={'center'}>
                        <Translate>{visitSettlementTranslationKeys.shared.form.sections.payment.header}</Translate>
                        {showPaymentEnabledToggle(payment) ? (
                           <PaymentEnabledToggle register={register} />
                        ) : (
                           <PaymentController register={register} name={'enabled'} />
                        )}
                     </StyledFlex>
                  }
               />

               <StyledFlex direction={'column'} gap={24}>
                  {showAdvancePaymentSection(visitSettlement) && visitDetails ? (
                     <AdvancePaymentTextView
                        advancePayments={visitSettlement.advancePayments!}
                        medicalServicesPrices={getMedicalServicePrices(visitSettlement.medicalServices)}
                        visitDetails={visitDetails}
                        onRefundSuccess={handleRefundSuccess}
                     />
                  ) : null}

                  {showPaymentFormPart(payment) && (
                     <StyledFlex direction={'column'} gap={'24px'}>
                        <StyledFlex direction={'column'} gap={'8px'}>
                           <PaymentMethodRadioGroup register={register} />
                           {showPaymentMethodDescriptionInput(payment) && (
                              <PaymentMethodDescriptionInput register={register} />
                           )}
                        </StyledFlex>
                        <StyledFlex direction={'row'} gap={'12px'}>
                           <PaymentAmountInput
                              register={register}
                              trigger={trigger}
                              advancePayments={visitSettlement.advancePayments}
                              medicalServicesPrices={getMedicalServicePrices(visitSettlement.medicalServices)}
                           />
                           {showPaymentCorrectlyCheckbox(payment, visitSettlement, adjustment) && (
                              <StyledMargin top={37}>
                                 <PaymentAcceptedCorrectlyCheckbox register={register} />
                              </StyledMargin>
                           )}
                        </StyledFlex>

                        {props.fiscalPrintEnabled ? <TaxIdentifierForReceiptField register={register} /> : null}
                     </StyledFlex>
                  )}
               </StyledFlex>
            </div>
         </StyledForm>

         {props.children({
            handleSubmit: handleSubmit,
            formState: formState,
            payment: payment,
         })}
      </>
   );
};
